import { useMutation, useSuspenseQuery } from '@apollo/client';
import { produce } from 'immer';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import { CurrencyInput, MedicalCodeTag } from '@eluve/blocks';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  Icon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@eluve/components';
import {
  MedicalCodeTypesEnum,
  ProceduralMedicalCodeTypes,
} from '@eluve/graphql-types';

import {
  addBillingCodePriceMutation,
  getBillingCodePrices,
  locationBillingCodePricesFragment,
  removeBillingCodePriceMutation,
  updateBillingCodePriceMutation,
} from './LocationBillingCodes.operations';
import {
  BillingCodeOption,
  SearchableBillingCodes,
} from './SearchableBillingCodes';
import { useSearchAllMedicalCodes } from './useSearchBillingCodes';

// Types

type LocationPriceRow = {
  billingCodeId: string;
  code: string;
  codeType: MedicalCodeTypesEnum;
  description: string | null;
  locationId: string;
  price: number;
  tenantId: string;
};

// Private Components

type AddBillingCodeModalProps = {
  locationId: string;
  onOpenChange: (open: boolean) => void;
  onSubmit: (row: LocationPriceRow) => void;
  open: boolean;
  selectedCode?: BillingCodeOption;
  tenantId: string;
};

const AddBillingCodeModal: React.FC<AddBillingCodeModalProps> = ({
  locationId,
  onOpenChange,
  onSubmit,
  open,
  selectedCode,
  tenantId,
}) => {
  const [price, setPrice] = useState<string | undefined>(undefined);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (selectedCode == null || price == null || isNaN(Number(price))) {
      event.preventDefault();
      return;
    }

    const row = {
      tenantId,
      locationId,
      billingCodeId: selectedCode.id,
      code: selectedCode.code,
      codeType: selectedCode.codeType,
      description: selectedCode.description,
      price: Math.floor(Number(price) * 100),
    };
    onSubmit(row);
    setPrice(undefined);
    event.preventDefault();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" className="pt-9">
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          {selectedCode != null && (
            <Stack align="center" gap={2}>
              <MedicalCodeTag
                code={selectedCode.code}
                codeType={selectedCode.codeType}
              />
              <Text className="text-center">{selectedCode.description}</Text>
            </Stack>
          )}
          <CurrencyInput onChange={setPrice} value={price} />
          <DialogFooter className="sm:justify-center">
            <Button type="submit">Add Billing Code</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

type PriceCellProps = {
  billingCodeId: string;
  locationId: string;
  tenantId: string;
  value: string;
};

const PriceCell: React.FC<PriceCellProps> = ({
  billingCodeId,
  locationId,
  tenantId,
  value,
}) => {
  const toast = useToast();
  const [originalValue, setOriginalValue] = useState<string | undefined>(value);
  const [currentValue, setCurrentValue] = useState<string | undefined>(value);
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isDirty = currentValue !== originalValue;

  useEffect(() => {
    setCurrentValue(value);
    setOriginalValue(value);
  }, [value]);

  const [updateBillingCodePrice] = useMutation(updateBillingCodePriceMutation, {
    onError: () => {
      toast.error('We could not update the price.');
    },
    optimisticResponse: ({ billingCodeId, locationId, price, tenantId }) => {
      return {
        updateBillingCodeLocationPricesByPk: {
          __typename: 'BillingCodeLocationPrices' as const,
          billingCodeId,
          locationId,
          tenantId,
          price,
        },
      };
    },
  });

  const updatePrice = () => {
    updateBillingCodePrice({
      variables: {
        billingCodeId,
        locationId,
        price: Math.floor(Number(currentValue) * 100),
        tenantId,
      },
    });
  };

  const handleBlur = () => {
    if (isDirty && formRef.current?.reportValidity()) {
      updatePrice();
    }
  };

  const handleChange = (newValue: string) => {
    if (currentValue === originalValue) {
      setOriginalValue(currentValue);
    }
    setCurrentValue(newValue);
  };

  const handleRevert = () => {
    setCurrentValue(originalValue);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    inputRef.current?.blur();
    event.preventDefault();
  };

  return (
    <div className="relative">
      <form className="w-full" onSubmit={handleSubmit} ref={formRef}>
        <CurrencyInput
          onBlur={handleBlur}
          onChange={handleChange}
          ref={inputRef}
          value={currentValue}
        />
      </form>
      {isDirty && !inputRef.current?.validity.valid && (
        <Stack
          className="absolute top-0 right-0"
          direction="horizontal"
          align="stretch"
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" onClick={handleRevert}>
                <Icon name="RotateCcw" size="auto" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Revert changes</TooltipContent>
          </Tooltip>
        </Stack>
      )}
    </div>
  );
};

type RemoveBillingCodeButtonProps = {
  billingCodeId: string;
  locationId: string;
  tenantId: string;
};

const RemoveBillingCodeButton: React.FC<RemoveBillingCodeButtonProps> = ({
  billingCodeId,
  locationId,
  tenantId,
}) => {
  const toast = useToast();
  const [removeBillingCode] = useMutation(removeBillingCodePriceMutation, {
    variables: {
      billingCodeId,
      locationId,
      tenantId,
    },
    onError: () => toast.error('We could not remove the billing code.'),
    optimisticResponse: ({ billingCodeId, locationId, tenantId }) => {
      return {
        deleteBillingCodeLocationPricesByPk: {
          __typename: 'BillingCodeLocationPrices' as const,
          billingCodeId,
          locationId,
          tenantId,
        },
      };
    },
    update: (_cache) => {
      cacheUtils.updateFragment(
        {
          fragment: locationBillingCodePricesFragment,
          key: {
            id: locationId,
          },
        },
        (existing) => {
          if (!existing) {
            return existing;
          }

          return produce(existing, (draft) => {
            const index = draft.billing_code_prices.findIndex(
              (billingCodePrice) =>
                billingCodePrice.billingCodeId === billingCodeId,
            );
            if (index !== -1) {
              draft.billing_code_prices.splice(index, 1);
            }
          });
        },
      );
    },
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="outline" onClick={() => removeBillingCode()}>
          <Icon name="Trash" size="auto" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Remove code</TooltipContent>
    </Tooltip>
  );
};

// Constants

const cols = new ColDefBuilder<LocationPriceRow>()
  .defaultSortable('code', {
    cellRenderer: (row) => (
      <div className="ml-2">
        <MedicalCodeTag code={row.code} codeType={row.codeType} />
      </div>
    ),
  })
  .defaultSortable('description', 'Description')
  .defaultSortable('price', {
    cellRenderer: (row) => (
      <PriceCell
        billingCodeId={row.billingCodeId}
        locationId={row.locationId}
        tenantId={row.tenantId}
        value={(row.price / 100).toFixed(2)}
      />
    ),
  })
  .colDef({
    id: 'actions',
    cell: ({ row }) => (
      <Stack justify="end" gap={2} direction="horizontal">
        <RemoveBillingCodeButton
          billingCodeId={row.original.billingCodeId}
          locationId={row.original.locationId}
          tenantId={row.original.tenantId}
        />
      </Stack>
    ),
  })
  .build();

// Public Components

type LocationBillingCodesProps = {
  locationId: string;
  tenantId: string;
};

export const LocationBillingCodes: React.FC<LocationBillingCodesProps> = ({
  locationId,
  tenantId,
}) => {
  const toast = useToast();
  const [isAddingCode, setIsAddingCode] = useState(false);
  const [selectedCode, setSelectedCode] = useState<
    BillingCodeOption | undefined
  >(undefined);

  const { data } = useSuspenseQuery(getBillingCodePrices, {
    variables: { tenantId, locationId },
  });

  const searchMedicalCodes = useSearchAllMedicalCodes({
    codeTypes: ProceduralMedicalCodeTypes as MedicalCodeTypesEnum[],
  });

  const billingCodePrices = useMemo(
    () => data?.locationsByPk?.billing_code_prices ?? [],
    [data],
  );

  const billingCodes = useMemo(
    () =>
      billingCodePrices.map<BillingCodeOption>((billingCodePrice) => {
        const billingCode = billingCodePrice.medical_code;
        return {
          code: billingCode.code,
          codeType: billingCode.codeType,
          description: billingCode.description,
          id: billingCode.id,
          userGroupCodeConfigId: null,
          source: 'medical_codes',
        };
      }),
    [billingCodePrices],
  );

  const rows = useMemo(
    () =>
      billingCodePrices.map<LocationPriceRow>((billingCodePrice) => {
        const billingCode = billingCodePrice.medical_code;
        return {
          tenantId,
          locationId,
          billingCodeId: billingCode.id,
          code: billingCode.code,
          codeType: billingCode.codeType,
          description: billingCode.description,
          price: billingCodePrice.price,
        };
      }),
    [billingCodePrices, locationId, tenantId],
  );

  const [addBillingCode] = useMutation(addBillingCodePriceMutation, {
    onError: () => {
      toast.error('We could not add the billing code.');
    },
  });

  const handleCodeSubmitted = (row: LocationPriceRow) => {
    if (selectedCode == null) {
      return;
    }

    addBillingCode({
      variables: {
        billingCodeId: row.billingCodeId,
        locationId,
        price: row.price,
      },
      optimisticResponse: ({ billingCodeId, locationId, price }) => {
        return {
          insertBillingCodeLocationPricesOne: {
            __typename: 'BillingCodeLocationPrices' as const,
            tenantId,
            locationId,
            billingCodeId,
            price,
            medical_code: {
              __typename: 'MedicalCodes' as const,
              id: row.billingCodeId,
              code: row.code,
              codeType: row.codeType,
              description: row.description,
            },
          },
        };
      },
      update: (_cache) => {
        cacheUtils.updateFragment(
          {
            fragment: locationBillingCodePricesFragment,
            key: {
              id: locationId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }

            return produce(existing, (draft) => {
              draft.billing_code_prices.push({
                __typename: 'BillingCodeLocationPrices',
                tenantId,
                locationId,
                billingCodeId: row.billingCodeId,
                price: row.price,
                medical_code: {
                  __typename: 'MedicalCodes',
                  id: row.billingCodeId,
                  code: row.code,
                  codeType: row.codeType,
                  description: row.description,
                },
              });
            });
          },
        );
      },
    });

    setSelectedCode(undefined);
    setIsAddingCode(false);
  };

  const handleCodeSelected = (code: BillingCodeOption) => {
    setSelectedCode(code);
    setIsAddingCode(true);
  };

  return (
    <Stack gap={5}>
      <SearchableBillingCodes
        onCodeAdded={handleCodeSelected}
        selectedCodes={billingCodes}
        shouldRenderValue={false}
        searchCodes={searchMedicalCodes}
      />
      <AddBillingCodeModal
        locationId={locationId}
        onOpenChange={setIsAddingCode}
        onSubmit={handleCodeSubmitted}
        open={isAddingCode}
        selectedCode={selectedCode}
        tenantId={tenantId}
      />
      <DataTable
        columns={cols}
        data={rows}
        placeholder="No billing codes added yet."
      />
    </Stack>
  );
};
